import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    AthleteAdminBreadcrumbNav,
    AthleteAdminNavState,
    getTeamMemberBreadcrumbLabel,
    navigateToTeamMemberFromBreadcrumb,
    useAdminAthleteDisplayName,
    useAthleteStaffForAdminBreadcrumb,
} from "./AthleteAdminBreadcrumbNav";
import VideoLightbox from "./VideoLightbox";
import { standaloneVideoAnalytics, WORDPRESS_TAB } from "../services/courseEventService";
import { isAthlete } from "../utils/auth";
import { buildApiUrl } from "../utils/apiConfig";
import "../styles/athlete-admin-layout.css";
import "../styles/athlete-admin-scaled-wrapper.css";
import "../styles/tutoring.css";
import "../styles/athlete-admin-page-spacing.css";
import { apiClient } from "../utils/apiClient";

interface TutoringContent {
    id: number;
    slug: string;
    title: string;
    buttonLabel: string;
    buttonUrl: string;
    topGraphicBlock: string;
    bottomGraphicBlock: string;
    video1Image: string;
    video2Image: string;
    video1Url: string;
    video2Url: string;
}

interface CoachData {
    id?: string;
    firstName?: string;
    lastName?: string;
}

export default function TutoringPage() {
    const { athleteId } = useParams<{ athleteId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [content, setContent] = useState<TutoringContent | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [coachData, setCoachData] = useState<CoachData | null>(null);
    const [selectedVideo, setSelectedVideo] = useState<{ id: number; title: string; vimeoUrl: string } | null>(null);

    const isAdminViewingAthlete = !isAthlete() && athleteId;

    const navState = location.state as AthleteAdminNavState | null;
    const teamMemberId = navState?.teamMemberId;
    const teamMemberName = navState?.teamMemberName;

    const { staffId: athleteStaffId, staffName: athleteStaffName } = useAthleteStaffForAdminBreadcrumb(
        athleteId,
        Boolean(isAdminViewingAthlete && athleteId)
    );

    const handleNavigateToTeamMember = () => {
        navigateToTeamMemberFromBreadcrumb(navigate, navState?.teamMemberId, athleteStaffId);
    };

    const decodeHtmlEntities = (text: string): string => {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
    };

    useEffect(() => {
        if (teamMemberName) {
            const nameParts = teamMemberName.split(' ');
            setCoachData({
                id: teamMemberId,
                firstName: nameParts[0] || teamMemberName,
                lastName: nameParts.slice(1).join(' ') || '',
            });
        } else {
            setCoachData(null);
        }
    }, [teamMemberId, teamMemberName]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await apiClient(
                    buildApiUrl('/api/tutoring/content'),
                    {
                        headers: { Accept: "application/json" },
                        credentials: "include"
                    }
                );

                if (!response.ok) {
                    throw new Error(`Failed to fetch tutoring content: ${response.status}`);
                }

                const data = await response.json();
                if (Array.isArray(data) && data.length > 0) {
                    const contentData = data[0];
                    if (contentData.title) {
                        contentData.title = decodeHtmlEntities(contentData.title);
                    }
                    setContent(contentData);
                } else {
                    setError("No tutoring content available");
                }
            } catch (err) {
                console.error("Failed to fetch tutoring content:", err);
                setError("Failed to load tutoring content");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleVideoClick = (videoUrl: string, title: string, videoId: number) => {
        setSelectedVideo({ id: videoId, title, vimeoUrl: videoUrl });
    };

    const teamMemberLabel = getTeamMemberBreadcrumbLabel(
        coachData,
        teamMemberName,
        athleteStaffName ?? undefined
    );

    const athleteLabel = useAdminAthleteDisplayName(athleteId, navState?.athleteDisplayName);

    const adminBreadcrumb = isAdminViewingAthlete ? (
        <AthleteAdminBreadcrumbNav
            pageLabel="Tutoring & Test Prep"
            teamMemberLabel={teamMemberLabel}
            onTeamMemberClick={handleNavigateToTeamMember}
            athleteLabel={athleteLabel}
            athleteProfileId={athleteId}
            data-testid="tutoring-breadcrumb"
        />
    ) : null;

    if (loading) {
        return (
            <div className={`tutoring-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
                {adminBreadcrumb}
                <div className={`tutoring-content ${isAdminViewingAthlete ? 'with-athlete-sidebar' : ''}`}>
                    <div className="tutoring-content-panel" data-testid="tutoring-content-panel">
                        <div className="tutoring-loading">Loading...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !content) {
        return (
            <div className={`tutoring-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
                {adminBreadcrumb}
                <div className={`tutoring-content ${isAdminViewingAthlete ? 'with-athlete-sidebar' : ''}`}>
                    <div className="tutoring-content-panel" data-testid="tutoring-content-panel">
                        <div className="tutoring-error">{error || "Failed to load content"}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`tutoring-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
            {adminBreadcrumb}

            <div className={`tutoring-content ${isAdminViewingAthlete ? 'with-athlete-sidebar' : ''}`}>
                <div className="tutoring-content-panel" data-testid="tutoring-content-panel">
                    <div className="tutoring-main-block">
                        <a
                            href={content.buttonUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tutoring-graphic-link"
                            data-testid="tutoring-graphic-link"
                        >
                            <img
                                src={content.topGraphicBlock}
                                alt="Polaris Student Athlete Consulting and Revolution Prep"
                                className="tutoring-graphic"
                            />
                        </a>
                    </div>

                    <div className="tutoring-partnership">
                        <img
                            src={content.bottomGraphicBlock}
                            alt="Polaris Student Athlete Consulting and Revolution Prep"
                            className="tutoring-graphic"
                        />
                    </div>

                    <div className="tutoring-videos">
                        <div
                            className="tutoring-video-card"
                            onClick={() => handleVideoClick(content.video1Url, "Why Revolution Prep?", content.id * 10 + 1)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleVideoClick(content.video1Url, "Why Revolution Prep?", content.id * 10 + 1);
                                }
                            }}
                            data-testid="tutoring-video-1"
                        >
                            <div className="video-thumbnail">
                                <img src={content.video1Image} alt="Why Revolution Prep?" />
                                <div className="video-play-overlay">
                                    <div className="video-play-button">▶</div>
                                </div>
                            </div>
                            <h3 className="video-title">Why Revolution Prep?</h3>
                        </div>

                        <div
                            className="tutoring-video-card"
                            onClick={() => handleVideoClick(content.video2Url, "How We Help You To Get There", content.id * 10 + 2)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleVideoClick(content.video2Url, "How We Help You To Get There", content.id * 10 + 2);
                                }
                            }}
                            data-testid="tutoring-video-2"
                        >
                            <div className="video-thumbnail">
                                <img src={content.video2Image} alt="How We Help You To Get There" />
                                <div className="video-play-overlay">
                                    <div className="video-play-button">▶</div>
                                </div>
                            </div>
                            <h3 className="video-title">How We Help You To Get There</h3>
                        </div>
                    </div>
                </div>
            </div>

            <VideoLightbox
                open={!!selectedVideo}
                resource={selectedVideo ?? undefined}
                onClose={() => setSelectedVideo(null)}
                analytics={selectedVideo ? standaloneVideoAnalytics(WORDPRESS_TAB.TUTORING, selectedVideo.id) : null}
            />
        </div>
    );
}
